import type { StructuredFilter } from "@media-viewer/shared";

/**
 * Returns true if the filter contains any non-cosmetic filtering criteria.
 * (rawQuery and warnings are bookkeeping, not filters.)
 */
function filterHasMeaningfulCriteria(filter: StructuredFilter): boolean {
  return Object.entries(filter).some(([k, v]) => {
    if (k === "rawQuery" || k === "warnings") return false;
    if (Array.isArray(v)) return v.length > 0;
    if (v && typeof v === "object") return Object.values(v).some((x) => x != null);
    return v != null;
  });
}

/** Browser-safe base64 of a JSON string. */
function b64encode(json: string): string {
  return btoa(unescape(encodeURIComponent(json)));
}
function b64decode(b64: string): string {
  return decodeURIComponent(escape(atob(b64)));
}

/**
 * Encode a StructuredFilter as a single base64-json `filter` param.
 * Returns null when there's nothing to filter on.
 */
export function filterToParams(filter: StructuredFilter | null): URLSearchParams | null {
  if (!filter) return null;
  if (!filterHasMeaningfulCriteria(filter)) return null;
  const params = new URLSearchParams();
  params.set("filter", b64encode(JSON.stringify(filter)));
  return params;
}

/** Encode a filter for URL state (object-shaped, suitable for TanStack Router search params). */
export function filterToUrlState(filter: StructuredFilter | null): Record<string, string> {
  const p = filterToParams(filter);
  return p ? { filter: p.get("filter")! } : {};
}

/** Decode `?filter=<base64-json>` from URL state. Returns null on any failure. */
export function filterFromUrlState(filterParam: string | undefined | null): StructuredFilter | null {
  if (!filterParam) return null;
  try {
    return JSON.parse(b64decode(filterParam)) as StructuredFilter;
  } catch {
    return null;
  }
}
